import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { HelpDrawer } from './HelpDrawer';
import { matchGuide } from './matchGuide';
import { DEFAULT_GUIDE_ID, GUIDES, getGuide } from './registry';

export interface HelpContextValue {
  isOpen: boolean;
  guideId: string;
  /** Open the drawer; without an id, on the guide for the current route. */
  openHelp: (id?: string) => void;
  closeHelp: () => void;
  showGuide: (id: string) => void;
}

const HelpContext = createContext<HelpContextValue | null>(null);

export function useHelp(): HelpContextValue {
  const value = useContext(HelpContext);
  if (!value) throw new Error('useHelp must be used within HelpProvider');
  return value;
}

export const HelpProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [guideId, setGuideId] = useState(DEFAULT_GUIDE_ID);

  const showGuide = useCallback((id: string) => {
    setGuideId(getGuide(id) ? id : DEFAULT_GUIDE_ID);
  }, []);

  const openHelp = useCallback(
    (id?: string) => {
      // The Help Center page supersedes the drawer, so never open on it.
      if (location.pathname.startsWith('/help')) return;
      showGuide(
        id ??
          matchGuide(
            { pathname: location.pathname, search: location.search },
            GUIDES
          ).id
      );
      setIsOpen(true);
    },
    [location.pathname, location.search, showGuide]
  );

  const closeHelp = useCallback(() => setIsOpen(false), []);

  const value = useMemo(
    () => ({ isOpen, guideId, openHelp, closeHelp, showGuide }),
    [isOpen, guideId, openHelp, closeHelp, showGuide]
  );

  return (
    <HelpContext.Provider value={value}>
      {children}
      <HelpDrawer />
    </HelpContext.Provider>
  );
};
